import React, { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  Row,
  Col,
  List,
  Button,
  Tag,
  Space,
  Spin,
  Empty,
  Tabs,
  Timeline,
  Modal,
  Rate,
  Form,
  Input,
  message,
} from 'antd';
import {
  ShoppingCartOutlined,
  TruckOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  QrcodeOutlined,
  DollarOutlined,
  StarOutlined,
} from '@ant-design/icons';
import { orderService, reviewService } from '../services/api';
import { useNavigate } from 'react-router-dom';
import './CustomerDashboard.css';

const { Title, Paragraph, Text } = Typography;
const { TabPane } = Tabs;

const Orders = () => {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [reviewModal, setReviewModal] = useState({ open: false, order: null, item: null });
  const [reviewForm] = Form.useForm();
  const [submittingReview, setSubmittingReview] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await orderService.getOrders();
      setOrders(response.data?.data || response.data || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  // Brand status colors: pending/packaging → gold, in-transit/confirmed → blue,
  // delivered/completed → green, cancelled/refunded → red.
  const getStatusColor = (status, paymentMethod, paymentStatus) => {
    if (paymentMethod === 'qr' && paymentStatus !== 'paid') {
      return '#f1a134';
    }
    const colors = {
      pending: '#f1a134',
      confirmed: '#2e9bd6',
      accepted: '#2e9bd6',
      processing: '#2e9bd6',
      packaging: '#f1a134',
      shipped: '#2e9bd6',
      out_for_delivery: '#2e9bd6',
      delivered: '#52b788',
      completed: '#52b788',
      cancelled: '#e5484d',
      refunded: '#e5484d',
      partially_refunded: '#2e9bd6',
    };
    return colors[status] || '#6b7280';
  };

  const getStatusIcon = (status, paymentMethod, paymentStatus) => {
    if (paymentMethod === 'qr' && paymentStatus !== 'paid') {
      return <QrcodeOutlined />;
    }
    const icons = {
      pending: <ClockCircleOutlined />,
      confirmed: <CheckCircleOutlined />,
      accepted: <CheckCircleOutlined />,
      processing: <TruckOutlined />,
      packaging: <TruckOutlined />,
      shipped: <TruckOutlined />,
      out_for_delivery: <TruckOutlined />,
      delivered: <CheckCircleOutlined />,
      completed: <CheckCircleOutlined />,
      cancelled: <ExclamationCircleOutlined />,
      refunded: <ExclamationCircleOutlined />,
      partially_refunded: <ExclamationCircleOutlined />,
    };
    return icons[status] || <ClockCircleOutlined />;
  };

  const getRefundStatusColor = (refundStatus) => {
    const colorMap = {
      none: '#6b7280',
      partial_refund_pending: '#f1a134',
      partially_refunded: '#2e9bd6',
      fully_refunded: '#52b788',
    };
    return colorMap[refundStatus] || '#6b7280';
  };

  const getRefundStatusLabel = (refundStatus) => {
    const labelMap = {
      none: 'No Refund',
      partial_refund_pending: 'Refund Pending',
      partially_refunded: 'Partially Refunded',
      fully_refunded: 'Fully Refunded',
    };
    return labelMap[refundStatus] || refundStatus || 'No Refund';
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'NPR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // A service booking reuses the order tables: the backend creates one order
  // with a single order_items row so the booking can be paid, invoiced and
  // rewarded. That row is a stand-in for the service, not a product, so it
  // must not be rendered as "Items / Qty". Older orders predate order_type,
  // hence the item-based fallback.
  const isServiceOrder = (order) => {
    if (!order) return false;
    if (order.order_type) return order.order_type === 'service';
    const items = order.items || [];
    return items.length === 1 && Boolean(items[0].product_service_id) && !items[0].product_id;
  };

  const serviceNameOf = (order) => {
    const item = (order.items || [])[0];
    return item?.product_service?.name || item?.item_name || item?.name || 'Service';
  };

  const formatServiceSchedule = (order) => {
    const parts = [];
    if (order.service_date) {
      const d = new Date(order.service_date);
      if (!Number.isNaN(d.getTime())) {
        parts.push(
          d.toLocaleDateString('en-IN', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          }),
        );
      }
    }
    if (order.service_time) {
      const t = new Date(order.service_time);
      if (!Number.isNaN(t.getTime())) {
        parts.push(
          t.toLocaleTimeString('en-IN', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          }),
        );
      }
    }
    return parts.join(' · ');
  };

  const trackOrder = (orderId) => {
    navigate(`/tracking/${orderId}`);
  };

  const getOrderStats = () => {
    const stats = {
      total: orders.length,
      pending: orders.filter((o) => (o.order_status || o.status) === 'pending').length,
      processing: orders.filter((o) =>
        ['accepted', 'packaging', 'out_for_delivery'].includes(o.order_status || o.status),
      ).length,
      delivered: orders.filter((o) => (o.order_status || o.status) === 'delivered').length,
      refunded: orders.filter((o) => o.refund_status && o.refund_status !== 'none').length,
    };
    return stats;
  };

  if (loading) {
    return (
      <div style={{ padding: '40px 24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const stats = getOrderStats();

  return (
    <div className="customer-page" style={{ padding: '0' }}>
      <div className="customer-page-header">
        <Title level={2}>My Orders</Title>
        <Paragraph>Track current orders and review past purchases.</Paragraph>
      </div>

      {/* Order Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={8} lg={{ flex: '1 1 0' }}>
          <Card
            className="customer-stat-card"
            styles={{ body: { textAlign: 'center', padding: '20px' } }}
          >
            <Text strong style={{ color: '#6b7280', fontSize: 13 }}>
              Total Orders
            </Text>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#2d5a3d', marginTop: 4 }}>
              {stats.total}
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={{ flex: '1 1 0' }}>
          <Card
            className="customer-stat-card"
            styles={{ body: { textAlign: 'center', padding: '20px' } }}
          >
            <Text strong style={{ color: '#6b7280', fontSize: 13 }}>
              Pending
            </Text>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#f1a134', marginTop: 4 }}>
              {stats.pending}
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={{ flex: '1 1 0' }}>
          <Card
            className="customer-stat-card"
            styles={{ body: { textAlign: 'center', padding: '20px' } }}
          >
            <Text strong style={{ color: '#6b7280', fontSize: 13 }}>
              Processing
            </Text>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#2e9bd6', marginTop: 4 }}>
              {stats.processing}
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={{ flex: '1 1 0' }}>
          <Card
            className="customer-stat-card"
            styles={{ body: { textAlign: 'center', padding: '20px' } }}
          >
            <Text strong style={{ color: '#6b7280', fontSize: 13 }}>
              Delivered
            </Text>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#52b788', marginTop: 4 }}>
              {stats.delivered}
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={{ flex: '1 1 0' }}>
          <Card
            className="customer-stat-card"
            styles={{ body: { textAlign: 'center', padding: '20px' } }}
          >
            <Text strong style={{ color: '#6b7280', fontSize: 13 }}>
              Refunded
            </Text>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#e5484d', marginTop: 4 }}>
              {stats.refunded}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Order Tabs */}
      <Tabs activeKey={activeTab} onChange={setActiveTab} style={{ marginBottom: 20 }}>
        <TabPane tab={`All Orders (${stats.total})`} key="all" />
        <TabPane tab={`Pending (${stats.pending})`} key="pending" />
        <TabPane tab={`Processing (${stats.processing})`} key="processing" />
        <TabPane tab={`Delivered (${stats.delivered})`} key="delivered" />
        <TabPane tab={`Refunded (${stats.refunded})`} key="refunded" />
      </Tabs>

      {/* Order List */}
      {(() => {
        const filteredOrders =
          activeTab === 'all'
            ? orders
            : orders.filter((o) => {
                const s = o.order_status || o.status;
                if (activeTab === 'pending') return s === 'pending';
                if (activeTab === 'processing')
                  return ['accepted', 'packaging', 'out_for_delivery'].includes(s);
                if (activeTab === 'delivered') return s === 'delivered';
                if (activeTab === 'refunded') return o.refund_status && o.refund_status !== 'none';
                return true;
              });
        return filteredOrders.length === 0 ? (
          <Card className="customer-card">
            <Empty description="No orders found" image={Empty.PRESENTED_IMAGE_SIMPLE}>
              <Button
                className="customer-btn-primary"
                onClick={() => navigate('/customer/products')}
              >
                Start Shopping
              </Button>
            </Empty>
          </Card>
        ) : (
          <List
            itemLayout="vertical"
            dataSource={filteredOrders}
            renderItem={(order) => {
              const ordStatus = order.order_status || order.status;
              const serviceOrder = isServiceOrder(order);
              return (
                <Card
                  key={order.id}
                  className="customer-card"
                  style={{ marginBottom: 16 }}
                  styles={{ header: { whiteSpace: 'normal' } }}
                  title={
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        rowGap: 12,
                        width: '100%',
                        whiteSpace: 'normal',
                        padding: '4px 0',
                      }}
                    >
                      <Space wrap size={[8, 8]}>
                        <Text strong>Order #{order.id}</Text>
                        <Tag
                          color={getStatusColor(
                            ordStatus,
                            order.payment_method,
                            order.payment_status,
                          )}
                          icon={getStatusIcon(
                            ordStatus,
                            order.payment_method,
                            order.payment_status,
                          )}
                          style={{ marginInlineEnd: 0 }}
                        >
                          {order.payment_method === 'qr' && order.payment_status !== 'paid'
                            ? 'AWAITING PAYMENT'
                            : ordStatus?.toUpperCase()}
                        </Tag>
                        {order.refund_status && order.refund_status !== 'none' && (
                          <Tag
                            color={getRefundStatusColor(order.refund_status)}
                            style={{ marginInlineEnd: 0 }}
                          >
                            {getRefundStatusLabel(order.refund_status)}
                          </Tag>
                        )}
                      </Space>
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          gap: 12,
                          rowGap: 8,
                        }}
                      >
                        {Number(order.refunded_amount || 0) > 0 ? (
                          <Space direction="vertical" size={0} align="end">
                            <Text delete type="secondary" style={{ fontSize: 13 }}>
                              {formatPrice(order.total_amount || order.total)}
                            </Text>
                            <Text strong style={{ fontSize: 18, color: '#e5484d' }}>
                              {formatPrice(order.net_amount || order.total_amount || order.total)}
                            </Text>
                            <Tag color="#e5484d" style={{ fontSize: 10, lineHeight: '16px' }}>
                              -{formatPrice(order.refunded_amount)}
                            </Tag>
                          </Space>
                        ) : (
                          <Text strong style={{ fontSize: 18, color: '#2d5a3d' }}>
                            {formatPrice(order.total_amount || order.total)}
                          </Text>
                        )}
                        <Button
                          className="customer-btn-primary"
                          icon={
                            order.payment_method === 'qr' && order.payment_status !== 'paid' ? (
                              <DollarOutlined />
                            ) : (
                              <EyeOutlined />
                            )
                          }
                          onClick={() => trackOrder(order.id)}
                        >
                          {order.payment_method === 'qr' && order.payment_status !== 'paid'
                            ? 'Pay Now'
                            : 'Track Order'}
                        </Button>
                      </div>
                    </div>
                  }
                >
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={16}>
                      <Paragraph>
                        <Text type="secondary">Order Date: </Text>
                        <Text>{formatDate(order.created_at)}</Text>
                      </Paragraph>

                      {serviceOrder ? (
                        <div style={{ marginTop: 16 }}>
                          <Text strong>Service: </Text>
                          <Text>{serviceNameOf(order)}</Text>
                          {formatServiceSchedule(order) && (
                            <>
                              <br />
                              <Text type="secondary">
                                Scheduled: {formatServiceSchedule(order)}
                              </Text>
                            </>
                          )}
                          {order.provider && (order.provider.name || order.provider.full_name) && (
                            <>
                              <br />
                              <Text type="secondary">
                                Provider: {order.provider.name || order.provider.full_name}
                              </Text>
                            </>
                          )}
                        </div>
                      ) : (
                        order.items &&
                        order.items.length > 0 && (
                          <div style={{ marginTop: 16 }}>
                            <Text strong>Items: </Text>
                            <List
                              size="small"
                              dataSource={order.items}
                              renderItem={(item) => {
                                const itemRefunds = (order.refunds || []).filter(
                                  (r) => r.order_item_id === item.id && r.status === 'processed',
                                );
                                const hasRefund = itemRefunds.length > 0;
                                return (
                                  <List.Item>
                                    <Row justify="space-between" style={{ width: '100%' }}>
                                      <Col>
                                        <Text>{item.product_service?.name || item.name}</Text>
                                        <br />
                                        <Text type="secondary">Qty: {item.quantity}</Text>
                                        {hasRefund && (
                                          <div style={{ marginTop: 4 }}>
                                            <Tag color="#e5484d" style={{ fontSize: 11 }}>
                                              Refunded: Rs.{' '}
                                              {itemRefunds
                                                .reduce(
                                                  (s, r) => s + parseFloat(r.refund_amount || 0),
                                                  0,
                                                )
                                                .toFixed(2)}
                                            </Tag>
                                            {itemRefunds.map((r, i) => (
                                              <div
                                                key={i}
                                                style={{
                                                  fontSize: 11,
                                                  color: '#9ca3af',
                                                  marginTop: 2,
                                                }}
                                              >
                                                {r.reason && (
                                                  <Text type="secondary">Reason: {r.reason}</Text>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </Col>
                                      <Col style={{ textAlign: 'right' }}>
                                        <Text delete={hasRefund}>
                                          {formatPrice(
                                            (item.price_at_order || item.price) * item.quantity,
                                          )}
                                        </Text>
                                        {hasRefund && (
                                          <div>
                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                              {item.quantity -
                                                Math.round(
                                                  itemRefunds.reduce(
                                                    (s, r) => s + parseFloat(r.refund_amount || 0),
                                                    0,
                                                  ) / (item.price_at_order || item.price),
                                                )}{' '}
                                              remaining
                                            </Text>
                                          </div>
                                        )}
                                      </Col>
                                    </Row>
                                  </List.Item>
                                );
                              }}
                            />
                          </div>
                        )
                      )}

                      {order.delivery_address && (
                        <div style={{ marginTop: 16 }}>
                          <Text strong>
                            {serviceOrder ? 'Service Address: ' : 'Delivery Address: '}
                          </Text>
                          <Text>
                            {order.delivery_address.full_name} -{' '}
                            {order.delivery_address.address_line1}
                          </Text>
                        </div>
                      )}

                      {/* Write Review Button for Completed Orders */}
                      {(ordStatus === 'delivered' || ordStatus === 'completed') && (
                        <div style={{ marginTop: 16 }}>
                          <Button
                            icon={<StarOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              setReviewModal({ open: true, order });
                            }}
                            style={{
                              borderRadius: 8,
                              background: '#fef4e3',
                              borderColor: '#f1a134',
                              color: '#e08a10',
                            }}
                          >
                            Write a Review
                          </Button>
                        </div>
                      )}

                      {order.payment_method === 'qr' && order.payment_status !== 'paid' && (
                        <div
                          style={{
                            marginTop: 16,
                            padding: 12,
                            background: '#fef4e3',
                            borderRadius: 8,
                            border: '1px solid #f7d9a0',
                          }}
                        >
                          <Space>
                            <DollarOutlined style={{ color: '#f1a134' }} />
                            <Text style={{ color: '#8a5a12' }}>
                              {order.payment_status === 'verification_pending'
                                ? 'Payment proof submitted. Awaiting admin verification.'
                                : 'Payment pending. Please complete payment and submit proof.'}
                            </Text>
                          </Space>
                          <br />
                          <Button
                            size="small"
                            type="link"
                            icon={<QrcodeOutlined />}
                            onClick={() => navigate(`/tracking/${order.id}`)}
                            style={{ marginTop: 4, padding: 0 }}
                          >
                            View Payment Details
                          </Button>
                        </div>
                      )}
                    </Col>

                    <Col xs={24} md={8}>
                      <Timeline>
                        <Timeline.Item color={ordStatus === 'pending' ? '#2e9bd6' : '#52b788'}>
                          Order Placed
                        </Timeline.Item>
                        {ordStatus !== 'pending' && (
                          <Timeline.Item color={ordStatus === 'cancelled' ? '#e5484d' : '#52b788'}>
                            Order Confirmed
                          </Timeline.Item>
                        )}
                        {['accepted', 'packaging', 'out_for_delivery', 'delivered'].includes(
                          ordStatus,
                        ) ? (
                          <Timeline.Item color={ordStatus === 'delivered' ? '#52b788' : '#2e9bd6'}>
                            Processing
                          </Timeline.Item>
                        ) : null}
                        {ordStatus === 'delivered' ? (
                          <Timeline.Item color="#52b788">Delivered</Timeline.Item>
                        ) : null}
                      </Timeline>
                    </Col>
                  </Row>
                </Card>
              );
            }}
          />
        );
      })()}

      {/* Review Modal */}
      <Modal
        title={`Write a Review - Order #${reviewModal.order?.id || ''}`}
        open={reviewModal.open}
        onCancel={() => {
          setReviewModal({ open: false, order: null, item: null });
          reviewForm.resetFields();
        }}
        footer={null}
        width={520}
        destroyOnClose
      >
        <Form
          form={reviewForm}
          layout="vertical"
          onFinish={async (values) => {
            if (!reviewModal.order) return;
            setSubmittingReview(true);
            try {
              const items = reviewModal.order.items || [];
              for (const item of items) {
                await reviewService.createReview({
                  order_id: reviewModal.order.id,
                  product_service_id: item.product_service_id,
                  rating: values.rating,
                  comment: values.comment || '',
                });
              }
              message.success('Review submitted successfully!');
              setReviewModal({ open: false, order: null, item: null });
              reviewForm.resetFields();
            } catch (error) {
              message.error(error.response?.data?.message || 'Failed to submit review');
            } finally {
              setSubmittingReview(false);
            }
          }}
        >
          <Form.Item
            name="rating"
            label="Rating"
            rules={[{ required: true, message: 'Please select a rating' }]}
          >
            <Rate style={{ fontSize: 28 }} />
          </Form.Item>
          <Form.Item name="comment" label="Review (optional)">
            <Input.TextArea rows={4} placeholder="Share your experience with this product..." />
          </Form.Item>
          {reviewModal.order?.items?.map((item) => (
            <Tag key={item.id} color="#2e9bd6" style={{ marginBottom: 4 }}>
              {item.product_service?.name || item.name}
            </Tag>
          ))}
          <Form.Item style={{ marginTop: 16 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={submittingReview}
              block
              style={{
                borderRadius: 8,
                background: 'linear-gradient(135deg, #52b788, #2d5a3d)',
                border: 'none',
                height: 44,
              }}
            >
              Submit Review
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {orders.length > 0 && (
        <div style={{ textAlign: 'center', marginTop: 32 }}>
          <Button
            className="customer-btn-primary"
            icon={<ShoppingCartOutlined />}
            onClick={() => navigate('/products')}
            size="large"
          >
            Continue Shopping
          </Button>
        </div>
      )}
    </div>
  );
};

export default Orders;
